/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/reviewCounts.scss';

import {ChangeFunction, Checkbox, CloseButton, Loader, stopPropagation} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, MouseEventHandler, useEffect, useImperativeHandle, useState} from 'react';
import classNames from 'classnames';
import {toPercentage} from "../../../util/utils";
import BolRating from "../../bolRating/bolRating";
import BolRatingBar from "../../bolmateRatingBar/bolRatingBar";
import {Rating} from "../../../util/types";

interface Props {
    onSelectRating?: (rating: Rating) => ChangeFunction;
    onClose?: MouseEventHandler;
    loading: boolean;
    total: number;
    counts: Record<Rating, number>;
    selectedRatings: Set<Rating>;
    className?: string;
}

export type ReviewCountsRef = {getCurrentSelected: () => Set<Rating>};

function ReviewCounts(props: Props, ref: ForwardedRef<ReviewCountsRef>) {
    const {loading, total, className, counts, selectedRatings, onClose, onSelectRating} = props;
    const [internalSelected, setInternalSelected] = useState(selectedRatings);

    function getCurrentSelected(): Set<Rating> {
        return internalSelected;
    }

    function _onSelectRating(rating: Rating) {
        return function(evt: React.ChangeEvent) {
            const internal = new Set(internalSelected);
            if (internal.has(rating)) internal.delete(rating);
            else internal.add(rating);
            setInternalSelected(internal);
            onSelectRating?.(rating)(evt);
        }
    }

    function renderRatingFilter(rating: Rating) {
        const ratingTotal = counts[rating];
        const active = internalSelected.has(rating);
        const percentage = !ratingTotal ? 0 : toPercentage(ratingTotal, total);
        return (
            <>
                <Checkbox checked={active} onCheck={_onSelectRating(rating)}/>
                <BolRating value={rating} staticStars/>
                <label> {ratingTotal}</label>
                <BolRatingBar value={percentage} className='rating-bar'/>
                <label> {percentage}%</label>
            </>
        )
    }

    useImperativeHandle(ref, () => ({getCurrentSelected}));

    useEffect(() => {
        setInternalSelected(selectedRatings);
    }, [selectedRatings]);

    return (
        <div className={classNames('review-counts-container', className)} onClick={stopPropagation}>
            <Loader active={loading}/>
            {!!onClose && <CloseButton onClick={onClose} className='btn-close'/>}
            <label className='title-text'>{total} Reviews</label>
            <div className='ratings-stats-container'>
                {renderRatingFilter(5)}
                {renderRatingFilter(4)}
                {renderRatingFilter(3)}
                {renderRatingFilter(2)}
                {renderRatingFilter(1)}
            </div>
        </div>
    );
}

export default forwardRef<ReviewCountsRef, Props>(ReviewCounts);